import { BASE_CR_PER_M3, RESOURCE_TIER, resourceCrPerUnit, type ResourceTier } from "../resourceCatalog";
import { DRONE_CATALOG } from "../droneSystem";

// Market catalog (Don's direction) -- 50 new tradeable commodities beyond
// the 9 real mining resources (resourceCatalog.ts), inspired by Drug
// Wars, Prosperous Universe, and Galactic Tycoons' commodity-trading
// loops. No unique mission items -- everything here is a generic,
// stackable good any pilot can buy, sell, loot, or haul. Stored in the
// exact same `state.cargo: Record<string, number>` bucket the 9 real
// resources already use -- no new cargo data model needed, just more
// possible keys in the same map. Deliberately kept at 1 unit = 1 m3
// (resourceCatalog.ts's volumePerUnitM3() returns 1 for anything not one
// of the 9 real resources) -- 16.40 gave ore real per-tier volume because
// that's what "EVE cargo and resource sizes" meant, but extending that to
// 50 flat-priced placeholder goods with no real per-item design work
// behind them yet would be inventing numbers, not adopting a model.
//
// **Naming rule (16.88, Don's direction): a unit's name has to be
// believable as one cubic metre.** Because the minimum is 1 m3, an item
// named for something pocket-sized reads as a bug -- a single wristwatch
// or a microscopic embryo occupying a cubic metre of hold. Ten items were
// renamed to the CONTAINER a trader actually buys (Timepiece Cases,
// Embryo Cryo-Canisters, Gemstone Parcels...) rather than prefixed with
// "Crate of": prefixing pushed names to ~31 characters against a
// then-maximum of 26, doubled the container noun ("Crate of ... Vials"),
// and would have clumped nine items together under "C" in the Market's
// alphabetical within-category sort. Mass nouns (Exotic Spice, Scrap
// Metal, Cryo-Stasis Gel) need nothing -- they are bulk by definition --
// and plurals whose cubic metre reads as a believable pallet were left
// alone deliberately (Quantum Capacitors, Ancient Data Cores, Unlicensed
// Firearms). Anything added here should pass the same test.
//
// Pricing (RESOLVED, interview pass -- design doc 16.60): every item's
// price is its CATEGORY's real tiered CR/m3 rate (BASE_CR_PER_M3,
// Section 16.2), the exact same 2.2x/tier curve the 9 real mining
// resources already use -- reusing an existing anchor rather than
// inventing a second, unrelated economy for trade goods. Replaces the
// earlier flat-1-CR/unit placeholder ("we will work on actual prices
// later"). See CATEGORY_TIER below for the category -> tier mapping.

export type MarketCategory =
  | "Contraband"
  | "Luxury"
  | "Industrial"
  | "Tech"
  | "Medical"
  | "Provisions"
  | "Salvage"
  | "Artifact"
  | "Munitions";

export interface MarketItem {
  name: string;
  category: MarketCategory;
  pricePerUnit: number;
}

// Category -> resource tier (0-4), assigned by narrative rarity/value --
// mirrors how the 9 real resources are tiered, not a separate scale.
// Provisions/Industrial (mundane bulk goods) sit at the bottom; Artifact
// (rare alien/precursor finds) sits at the top, matching how Artifact
// already outranks Salvage in wreck-loot rarity (expeditionScreen.ts's
// buildWreckLoot).
const CATEGORY_TIER: Record<MarketCategory, ResourceTier> = {
  Provisions: 0,
  Industrial: 0,
  Salvage: 1,
  Medical: 2,
  Munitions: 2,
  Tech: 2,
  Luxury: 3,
  Contraband: 3,
  Artifact: 4
};

function categoryPrice(category: MarketCategory): number {
  return BASE_CR_PER_M3[CATEGORY_TIER[category]];
}

const RAW_ITEMS: Array<{ name: string; category: MarketCategory }> = [
  // Contraband -- illicit goods, Drug Wars' spiritual core
  { name: "Synth-Narcotics", category: "Contraband" },
  { name: "Stimulant Vial Cases", category: "Contraband" },
  { name: "Black-Market Cybernetics", category: "Contraband" },
  { name: "Unlicensed Firearms", category: "Contraband" },
  { name: "Counterfeit Chip Cases", category: "Contraband" },

  // Luxury -- high-margin trade goods
  { name: "Exotic Spice", category: "Luxury" },
  { name: "Vintage Wine Casks", category: "Luxury" },
  { name: "Silk-Weave Fabric", category: "Luxury" },
  { name: "Gemstone Parcels", category: "Luxury" },
  { name: "Perfume Cases", category: "Luxury" },
  { name: "Fine Art Sculptures", category: "Luxury" },
  { name: "Timepiece Cases", category: "Luxury" },

  // Industrial -- bulk trade goods, Prosperous-Universe-style intermediates
  { name: "Textile Bolts", category: "Industrial" },
  { name: "Machine Parts", category: "Industrial" },
  { name: "Construction Polymers", category: "Industrial" },
  { name: "Synthetic Lumber", category: "Industrial" },
  { name: "Ceramic Composites", category: "Industrial" },
  { name: "Industrial Lubricant", category: "Industrial" },
  { name: "Insulation Foam", category: "Industrial" },
  { name: "Structural Alloy Plating", category: "Industrial" },

  // Tech -- ship/station component goods
  { name: "Microprocessor Arrays", category: "Tech" },
  { name: "Holographic Displays", category: "Tech" },
  { name: "Quantum Capacitors", category: "Tech" },
  { name: "Sensor Arrays", category: "Tech" },
  { name: "Navigation Chip Trays", category: "Tech" },
  { name: "AI Cortex Modules", category: "Tech" },
  { name: "Power Cells", category: "Tech" },

  // Medical
  { name: "Trauma Kit Pallets", category: "Medical" },
  { name: "Synthetic Blood Plasma", category: "Medical" },
  { name: "Antiviral Serum Cases", category: "Medical" },
  { name: "Cryo-Stasis Gel", category: "Medical" },
  { name: "Pharmaceutical Compounds", category: "Medical" },

  // Provisions -- foodstuffs distinct from the generic "Provisions" pool
  { name: "Hydroponic Produce", category: "Provisions" },
  { name: "Preserved Rations", category: "Provisions" },
  { name: "Algae Protein Cakes", category: "Provisions" },
  { name: "Synthetic Coffee Beans", category: "Provisions" },
  { name: "Embryo Cryo-Canisters", category: "Provisions" },

  // Salvage -- the primary wreck-loot pool (see expeditionScreen.ts's
  // buildWreckLoot)
  { name: "Scrap Metal", category: "Salvage" },
  { name: "Derelict Hull Plating", category: "Salvage" },
  { name: "Burnt-Out Circuitry", category: "Salvage" },
  { name: "Salvaged Wiring Harnesses", category: "Salvage" },
  { name: "Broken Drone Parts", category: "Salvage" },
  { name: "Stripped Alloy Frames", category: "Salvage" },

  // Artifact -- rarer wreck/anomaly loot
  { name: "Alien Relic Fragments", category: "Artifact" },
  { name: "Ancient Data Cores", category: "Artifact" },
  { name: "Xeno-Crystal Shards", category: "Artifact" },
  { name: "Precursor Tech Housing", category: "Artifact" },
  { name: "Alloy Sample Cases", category: "Artifact" },

  // Munitions
  { name: "Plasma Munitions", category: "Munitions" },
  { name: "Warhead Casings", category: "Munitions" },

  // Fuel and Provisions themselves -- tradeable like any other good, and
  // also this Market's own price anchor for real fuel (16.50, src/fuel.ts)
  // and Distress Signal (16.54, src/distressSignal.ts) purchases. Bought/
  // sold into the same general cargo hold for now, not a separate capacity
  // bar -- there's no code path today that moves a purchased "Fuel" cargo
  // stack into the real fuelOnBoard pool.
  { name: "Fuel", category: "Provisions" },
  { name: "Provisions", category: "Provisions" }
];

// Drones (16.74) are the one catalog entry whose price is NOT its
// category's tiered CR/m3 rate: a drone is a piece of equipment with a
// real combat role, priced off the hull it serves (see DRONE_CATALOG's
// own derivation), not off a trade-goods curve. Appended here rather
// than kept in a separate list so every existing consumer -- Market,
// Cargo UI, wreck loot pricing -- picks them up with no changes.
export const MARKET_ITEMS: MarketItem[] = [
  ...RAW_ITEMS.map((i) => ({ ...i, pricePerUnit: categoryPrice(i.category) })),
  ...DRONE_CATALOG.map((d) => ({
    name: d.name,
    category: "Munitions" as MarketCategory,
    pricePerUnit: d.priceCr
  }))
];

// One reconciled price lookup covering both the 9 real mining resources
// (real tiered CR/m3 converted to real CR/unit via 16.40's per-tier
// volume, Section 16.2 -- unchanged, not overridden by the new flat-1
// catalog) and the 50 new market goods above. Every screen that prices
// cargo (Market, Cargo UI) uses this, not resourceCrPerM3/resourceCrPerUnit
// directly, so a looted/bought good never crashes an unrelated lookup, and
// every caller can treat the return value as real CR per STORED UNIT
// without needing to separately know about volume.
export function marketPricePerUnit(name: string): number {
  if (RESOURCE_TIER[name] !== undefined) {
    return resourceCrPerUnit(name);
  }
  const item = MARKET_ITEMS.find((i) => i.name === name);
  return item?.pricePerUnit ?? 1; // unreachable in practice -- every real name is either a resource or a catalog item above
}

// ===========================================================================
// 16.92: per-item price variation, and a real buy/sell spread
// ===========================================================================

/**
 * A permanent, per-item price multiplier in [0.75, 1.25].
 *
 * Fixes the flatness Don flagged: every item in a category priced identically
 * meant "which Artifact do I carry" had no answer, and 53 distinct goods
 * collapsed into 9 prices. Now Alien Relic Fragments and Alloy Sample Cases
 * are worth genuinely different amounts.
 *
 * Derived from the item's NAME and therefore stable forever -- never re-rolled,
 * not stored, identical on every device and after every reload. That
 * permanence is the point: a player can learn "Alien Relic Fragments are the
 * good Artifact" and have it stay true. The per-station, per-hour variation
 * that makes hauling work is a SEPARATE multiplier (16.92 phase 3); this one
 * is the item's own intrinsic worth.
 *
 * FNV-1a because it needs to be stable across builds and platforms, which
 * rules out anything involving Math.random or hash-order iteration.
 */
export function itemPriceIndex(name: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < name.length; i++) {
    hash ^= name.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  // 0.75 .. 1.25, in 0.01 steps so displayed prices stay tidy.
  return 0.75 + ((hash % 51) / 100);
}

/**
 * Buy/sell spread by category (Don's ruling: 20% headline, 10-22% by
 * category).
 *
 * Varying rather than flat, for the same reason prices vary: a single number
 * everywhere is the flatness we just removed. The ordering is liquidity --
 * bulk staples that everyone trades have tight spreads, and goods with one
 * buyer in the sector have wide ones. Provisions are the tightest thing in the
 * game; Artifacts have no market at all, only collectors.
 */
export const CATEGORY_SPREAD: Record<MarketCategory, number> = {
  Provisions: 0.1,
  Industrial: 0.1,
  Salvage: 0.12,
  Medical: 0.15,
  Munitions: 0.15,
  Tech: 0.15,
  Luxury: 0.18,
  Contraband: 0.22,
  Artifact: 0.25
};

/** Mined resources are commodities -- the tightest spread, like Provisions. */
const RESOURCE_SPREAD = 0.1;

/**
 * Fitted equipment rather than tradeable cargo (Don's ruling, 16.94).
 *
 * Defined here, beside the catalogue, and imported by the station market so
 * there is exactly ONE answer to "is this a commodity". Two definitions would
 * have drifted immediately: the first version of this change made the station
 * prices flat while these item-level functions still applied a 15% Munitions
 * spread, so the same drone was worth two different things depending on which
 * screen asked.
 */
export function isFixedPriceEquipment(name: string): boolean {
  return DRONE_CATALOG.some((d) => d.name === name);
}

export function spreadFor(name: string): number {
  // No spread on equipment: buying and reselling a part costs nothing but
  // time, and with no spread there is no trade to arbitrage in the first place.
  if (isFixedPriceEquipment(name)) return 0;
  if (RESOURCE_TIER[name] !== undefined) return RESOURCE_SPREAD;
  const item = MARKET_ITEMS.find((i) => i.name === name);
  return item ? CATEGORY_SPREAD[item.category] : 0.15;
}

/**
 * What a station charges you for one unit -- the reference price with the
 * item's own index applied.
 */
export function marketBuyPricePerUnit(name: string): number {
  // Equipment carries no per-item variation either: a Capital Drone is a
  // catalogue part with a catalogue price, not a good whose worth depends on
  // which one it is.
  if (isFixedPriceEquipment(name)) return marketPricePerUnit(name);
  return marketPricePerUnit(name) * itemPriceIndex(name);
}

/**
 * What a station pays you for one unit: the buy price less the category
 * spread. Always strictly less than the buy price, which is what stops the
 * Market being a money printer.
 */
export function marketSellPricePerUnit(name: string): number {
  return marketBuyPricePerUnit(name) * (1 - spreadFor(name));
}

export function itemsByCategory(category: MarketCategory): MarketItem[] {
  return MARKET_ITEMS.filter((i) => i.category === category);
}

/**
 * Old item name -> new item name, for the 16.88 renames.
 *
 * Load-bearing, not cosmetic: `state.cargo` and a Base's `storage` are keyed
 * by the item's NAME, so a rename would otherwise orphan every existing stack.
 * An orphaned key doesn't error -- it does something worse and quieter. It
 * still shows in the Cargo screen, but `marketPricePerUnit` falls through to
 * its 1 CR fallback and the Market never lists it, so the captain is left
 * holding cargo they can see, cannot sell, and have no explanation for.
 */
export const RENAMED_MARKET_ITEMS: Record<string, string> = {
  "Neural Stimulant Vials": "Stimulant Vial Cases",
  "Counterfeit Currency Chips": "Counterfeit Chip Cases",
  "Rare Gemstones": "Gemstone Parcels",
  "Designer Perfume": "Perfume Cases",
  "Luxury Timepieces": "Timepiece Cases",
  "Navigation Chips": "Navigation Chip Trays",
  "Antiviral Serum": "Antiviral Serum Cases",
  "Trauma Kits": "Trauma Kit Pallets",
  "Livestock Embryos": "Embryo Cryo-Canisters",
  "Unidentified Alloy Samples": "Alloy Sample Cases"
};

/**
 * Rewrite the keys of an item-keyed quantity map through the rename table.
 * Quantities are ADDED rather than overwritten, so a save that somehow holds
 * both the old and the new name (hand-edited, or written either side of an
 * in-flight sync) ends up with the captain's real total instead of silently
 * dropping one of the two stacks. Untouched maps are returned as a copy, so
 * callers can assign the result unconditionally.
 */
export function migrateRenamedItems(items: Record<string, number>): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [name, qty] of Object.entries(items)) {
    const key = RENAMED_MARKET_ITEMS[name] ?? name;
    out[key] = (out[key] ?? 0) + qty;
  }
  return out;
}

// Pirate-Friendly standing (16.55) unlocks a discount on Contraband-
// category goods at any Market, mirroring how Friendly standing with a
// real faction already unlocks better Market/Shipyard tiers (16.48). 20%
// off is a placeholder magnitude -- the doc locks that the discount
// exists, not its exact size ("not yet balanced," same framing as every
// other flat number in this interview pass).
/**
 * Pirate-Friendly Contraband discount (16.55), reduced from 0.8 to 0.9 in
 * 16.92 to obey an invariant that had been broken since it was written:
 *
 *   A one-sided buy discount must be strictly SMALLER than the category
 *   spread, or it is an arbitrage.
 *
 * With no spread at all, a 20% buy discount against an undiscounted sell price
 * was an infinite credit printer: buy Contraband at 0.8x, sell it back at the
 * same station for 1.0x, repeat. 16.92's 22% Contraband spread closes that on
 * its own, but only by 2 percentage points -- close enough that any later tweak
 * to either number reopens it. At 0.9x the margin is 12 points, and the benefit
 * now pays off the way it should: when the captain HAULS contraband somewhere
 * that wants it, not by flipping on the spot.
 */
export const CONTRABAND_DISCOUNT_MULTIPLIER = 0.9;

export function contrabandDiscountApplies(name: string, pirateStandingIsFriendly: boolean): boolean {
  if (!pirateStandingIsFriendly) return false;
  const item = MARKET_ITEMS.find((i) => i.name === name);
  return item?.category === "Contraband";
}

// Same lookup as marketPricePerUnit, with the Contraband discount folded
// in when the captain's Pirate standing is Friendly (16.55). A separate
// function rather than an optional param on marketPricePerUnit so every
// existing call site (which has no reason to know about Pirate standing)
// is unaffected.
export function marketPricePerUnitFor(name: string, pirateStandingIsFriendly: boolean): number {
  // Based on the BUY price (16.92), not the bare reference price: otherwise the
  // discounted figure the Buy button charges and the price the catalog row
  // displays are computed from different bases and silently disagree.
  const base = marketBuyPricePerUnit(name);
  return contrabandDiscountApplies(name, pirateStandingIsFriendly) ? base * CONTRABAND_DISCOUNT_MULTIPLIER : base;
}
